import { useCallback, useMemo, useEffect, useState } from "react";
import DropDownList from "../../components/Atomos/DropDown/Dropdown";
import Input from "../../components/Atomos/Inputs/Input";
import {
  useEventForm,
  useEventAddressForm,
} from "../../hooks/Events/useEventForm";
import { UseCountryList } from "../../hooks/Catalogs/useCountry";
import { useStatesList } from "../../hooks/Catalogs/useStates";
import SwitchToggle from "../../components/Atomos/Switch-Toogle/Switch";
import { useEventValidation } from "../../hooks/Events/useEventValidation";
import { getCitiesByState, getCitiesListOptions } from "../../Services/City";
import { ICity } from "../../interfaces/models/ICity";

interface LocationProps {
  validation?: ReturnType<typeof useEventValidation>;
}

const Location = ({ validation }: LocationProps) => {
  const { event, updateEventField } = useEventForm();
  const { address, updateEventAddressField, updateEventAddress } = useEventAddressForm();
  const localValidation = useEventValidation();
  const validationHook = validation || localValidation;
  const { countryData, isFetching: isFetchingCountries } = UseCountryList();
  const { statesData, isFetching: isFetchingStates } = useStatesList();
  const [cityData, setCityData] = useState<ICity[]>([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const sortedCountryData = useMemo(() => {
    return [...countryData].sort((a, b) => 
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: 'base' })
    );
  }, [countryData]);

  const filteredStatesData = useMemo(() => {
    if (!event.address?.countryName) {
      return [];
    }

    const selectedCountry = countryData.find(
      (country) => country.name === event.address?.countryName
    );

    if (!selectedCountry) {
      return [];
    }

    const filtered = statesData.filter(
      (state) =>
        state.country === selectedCountry.id ||
        state.countryName === selectedCountry.name ||
        state.countryName === event.address?.countryName
    );

    if (event.address?.stateName) {
      const selectedStateInAll = statesData.find(
        (state) => state.name === event.address?.stateName
      );
      if (
        selectedStateInAll &&
        !filtered.find((s) => s.name === event.address?.stateName)
      ) {
        filtered.push(selectedStateInAll);
      }
    }

    return filtered.sort((a, b) => 
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: 'base' })
    );
  }, [
    countryData,
    statesData,
    event.address?.countryName,
    event.address?.stateName,
  ]);

  const countryHasStates = useMemo(() => {
    if (!event.address?.countryName) {
      return false;
    }
    return filteredStatesData.length > 0;
  }, [event.address?.countryName, filteredStatesData]);

  const loadCities = useCallback(async () => {
    if (!event.address?.countryName) {
      setCityData([]);
      return;
    }

    if (isFetchingCountries || isFetchingStates) {
      return;
    }

    if (countryData.length === 0) {
      return;
    }

    setIsLoadingCities(true);
    try {
      const selectedCountry = countryData.find(
        (country) => country.name === event.address?.countryName
      );
      
      if (!selectedCountry) {
        setCityData([]);
        return;
      }

      if (event.address?.stateName) {
        let selectedState = filteredStatesData.find(
          (state) => state.name === event.address?.stateName
        );

        if (!selectedState && statesData.length > 0) {
          const countryStates = statesData.filter(
            (state) =>
              state.country === selectedCountry.id ||
              state.countryName === selectedCountry.name ||
              state.countryName === event.address?.countryName
          );
          selectedState = countryStates.find(
            (state) => state.name === event.address?.stateName
          );
        }

        if (!selectedState && statesData.length > 0) {
          selectedState = statesData.find(
            (state) => state.name === event.address?.stateName
          );
        }

        if (selectedState?.id) {
          const cities = await getCitiesByState(selectedState.id);
          setCityData(cities || []);
          return;
        }

        if (statesData.length === 0) {
          return;
        }

        setCityData([]);
        return;
      }

      const countryStates = statesData.filter(
        (state) =>
          state.country === selectedCountry.id ||
          state.countryName === selectedCountry.name ||
          state.countryName === event.address?.countryName
      );

      const hasStates = countryStates.length > 0;

      if (!hasStates && event.address?.countryName) {
        if (selectedCountry?.id) {
          const filter = {
            country: { value: selectedCountry.id, comparisonType: 'EQUAL' }
          };
          const cities = await getCitiesListOptions(filter);
          const formattedCities = cities?.map((city: any) => ({
            id: city.id,
            name: city.name,
            country: selectedCountry.id,
            countryName: selectedCountry.name,
          })) || [];
          setCityData(formattedCities);
        } else {
          setCityData([]);
        }
      } else if (!event.address?.stateName) {
        setCityData([]);
      } else {
        setCityData([]);
      }
    } catch (error) {
      console.error("Error loading cities:", error);
      setCityData([]);
    } finally {
      setIsLoadingCities(false);
    }
  }, [
    event.address?.countryName,
    event.address?.stateName,
    filteredStatesData,
    countryData,
    statesData,
    isFetchingCountries,
    isFetchingStates,
  ]);

  const filteredCityData = useMemo(() => {
    if (cityData.length === 0) {
      return [];
    }

    if (event.address?.cityName) {
      const selectedCityInList = cityData.find(
        (city) => city.name === event.address?.cityName
      );
      if (!selectedCityInList) {
        return cityData.sort((a, b) => 
          (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: 'base' })
        );
      }
    }

    return cityData.sort((a, b) => 
      (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: 'base' })
    );
  }, [cityData, event.address?.cityName]);

  const handleComingSoonToggle = useCallback(() => {
    updateEventField("comingSoonLocation", !event?.comingSoonLocation);
  }, [updateEventField, event?.comingSoonLocation]);

  const handleComingSoonMessageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      updateEventField("comingSoonLocationMessage", e.target.value);
    },
    [updateEventField]
  );

  const getFieldError = useCallback((fieldName: string) => {
    if (!validationHook.hasAttemptedSave) {
      return undefined;
    }
    return validationHook.errors[fieldName];
  }, [validationHook.hasAttemptedSave, validationHook.errors]);

  const hasFieldError = useCallback((fieldName: string) => {
    return validationHook.hasAttemptedSave && !!validationHook.errors[fieldName];
  }, [validationHook.hasAttemptedSave, validationHook.errors]);

  const updateFieldWithValidation = useCallback((
    fieldName: string,
    value: any
  ) => {
    updateEventField(fieldName as any, value);
    if (validationHook.hasAttemptedSave) {
      validationHook.validateField(fieldName, value, event);
    }
  }, [updateEventField, validationHook, event]);

  const updateAddressFieldWithValidation = useCallback((
    fieldName: string,
    value: any
  ) => {
    updateEventAddressField(fieldName as any, value);
    if (validationHook.hasAttemptedSave) {
      validationHook.validateField(fieldName, value, event);
    }
  }, [updateEventAddressField, validationHook, event]);

  useEffect(() => {
    if (!event.address?.countryName) {
      if (event.address?.stateName) {
        updateEventAddressField("stateName", "");
      }
      if (event.address?.cityName) {
        updateEventAddressField("cityName", "");
      }
    }
  }, [event.address?.countryName, event.address?.stateName, event.address?.cityName, updateEventAddressField]);

  useEffect(() => {
    if (isFetchingStates) {
      return;
    }

    if (event.address?.countryName && event.address?.stateName) {
      const selectedState = filteredStatesData.find(
        (state) => state.name === event.address?.stateName
      );
      if (!selectedState) {
        updateEventAddressField("stateName", "");
      }
    }
  }, [event.address?.countryName, event.address?.stateName, filteredStatesData, updateEventAddressField, isFetchingStates]);

  useEffect(() => {
    if (isFetchingCountries || isFetchingStates || isLoadingCities) {
      return;
    }

    if (event.address?.countryName && countryHasStates) {
      if (!event.address?.stateName && event.address?.cityName) {
      }
    }
  }, [event.address?.countryName, countryHasStates, event.address?.stateName, event.address?.cityName, updateEventAddressField, isFetchingCountries, isFetchingStates, isLoadingCities]);

  useEffect(() => {
    if (isFetchingCountries || isFetchingStates) {
      return;
    }

    if (event.address?.countryName && !countryHasStates && event.address?.stateName) {
      updateEventAddressField("stateName", "");
    }
  }, [event.address?.countryName, countryHasStates, event.address?.stateName, updateEventAddressField, isFetchingCountries, isFetchingStates]);

  useEffect(() => {
    if (isFetchingCountries || isFetchingStates || isLoadingCities) {
      return;
    }


  }, [event.address?.countryName, countryHasStates, event.address?.cityName, updateEventAddressField, isFetchingCountries, isFetchingStates, isLoadingCities]);

  useEffect(() => {
    if (isFetchingCountries) {
      return;
    }

    if (countryData.length === 0) {
      return;
    }

    if (!event.address?.countryName) {
      setCityData([]);
      return;
    }

    const selectedCountry = countryData.find(
      (country) => country.name === event.address?.countryName
    );

    if (!selectedCountry) {
      setCityData([]);
      return;
    }

    if (event.address?.stateName) {
      if (isFetchingStates) {
        return;
      }
      loadCities();
      return;
    }

    if (isFetchingStates) {
      return;
    }

    const countryStates = statesData.filter(
      (state) =>
        state.country === selectedCountry.id ||
        state.countryName === selectedCountry.name ||
        state.countryName === event.address?.countryName
    );

    const hasStates = countryStates.length > 0;

    if (!hasStates) {
      loadCities();
    } else {
      setCityData([]);
    }
  }, [
    event.address?.countryName, 
    event.address?.stateName, 
    filteredStatesData,
    countryData,
    statesData,
    loadCities,
    isFetchingCountries,
    isFetchingStates
  ]);

  return (
    <div className="flex flex-col gap-8 ">
      <div className="flex flex-col gap-2 md:gap-[.625rem]">
        <SwitchToggle
          className=""
          checked={!!event?.comingSoonLocation}
          seter={handleComingSoonToggle}
          titleFirst
          title="Coming Soon Location"
        />
        {event?.comingSoonLocation && (
          <div className="w-full lg:w-1/2">
            <Input
              title=""
              error={false}
              name="comingSoonMessage"
              value={event.comingSoonLocationMessage}
              placeholder="Coming Soon"
              onChange={handleComingSoonMessageChange}
            />
          </div>
        )}
      </div>
      {!event?.comingSoonLocation && (
        <div className="grid grid-cols-4 gap-8">
          <div className="col-span-4 lg:col-span-2">
            <Input
              title="Location"
              error={hasFieldError("location")}
              errorMsj={getFieldError("location") || ""}
              name="location"
              value={event.location || ""}
              onChange={(e) => updateFieldWithValidation("location", e.target.value)}
              placeholder="Building Name, Hotel, Convention Center"
            />
          </div>
          <div className="col-span-4 lg:col-span-2">
            <Input
              title="Venue Link"
              error={false}
              errorMsj={""}
              name="venueLink"
              value={event.venueLink || ""}
              onChange={(e) => updateEventField("venueLink", e.target.value)}
            />
          </div>
          <div className="col-span-4 lg:col-span-2">
            <Input
              title="Address 1"
              error={false}
              errorMsj={""}
              name="address1"
              value={address?.address1 || ""}
              onChange={(e) =>
                updateEventAddressField("address1", e.target.value)
              }
              placeholder="Street Name, Number"
            />
          </div>
          <div className="col-span-4 lg:col-span-2">
            <Input
              title="Address 2"
              error={false}
              errorMsj={""}
              name="address2"
              value={address?.address2 || ""}
              onChange={(e) =>
                updateEventAddressField("address2", e.target.value)
              }
              placeholder="Street Name, Number"
            />
          </div>
          <div className="col-span-4 lg:col-span-1">
            <DropDownList
              title="Country"
              required={true}
              selectedObjst={event.address?.countryName || ""}
              error={hasFieldError("countryName")}
              errormsj={getFieldError("countryName")}
              seter={(e) => {
                const currentCountry = event.address?.countryName || "";
                const newCountry = e || "";
                if (newCountry !== currentCountry) {
                  updateEventAddress({
                    countryName: newCountry,
                    stateName: "",
                    cityName: "",
                  });
                } else {
                  updateAddressFieldWithValidation("countryName", newCountry);
                }
              }}
              data={sortedCountryData}
            />
          </div>
          <div className="col-span-4 lg:col-span-1">
            <DropDownList
              key={`state-${event.address?.countryName || 'none'}`}
              title="State / Province"
              selectedObjst={event.address?.stateName || ""}
              disabled={!event.address?.countryName}
              seter={(e) => {
                const currentState = event.address?.stateName || "";
                const newState = e || "";
                if (newState !== currentState) {
                  updateEventAddress({
                    stateName: newState,
                    cityName: "",
                  });
                } else {
                  updateEventAddressField("stateName", newState);
                }
              }}
              data={filteredStatesData}
            />
          </div>
          <div className="col-span-4 lg:col-span-1">
            <Input
              title="City"
              required={true}
              error={hasFieldError("cityName")}
              errorMsj={getFieldError("cityName") || ""}
              name="cityName"
              value={event.address?.cityName || ""}
              onChange={(e) => updateAddressFieldWithValidation("cityName", e.target.value)}
            />
          </div>
          <div className="col-span-4 lg:col-span-1">
            <Input
              title="Zip Code"
              error={false}
              errorMsj={""}
              name="zipCode"
              value={address?.zipCode || ""}
              onChange={(e) =>
                updateEventAddressField("zipCode", e.target.value)
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Location;
